import React from 'react';
import { SessionLabel } from '../../types/latestPrice';

/**
 * "Latest price (delayed)" chip (glossary stock_price_live).
 *
 * Secondary to the Price: it only formats what the backend sent. The data plan
 * is 15 minutes delayed, so the chip always states its delay and New York time
 * and never says "Live".
 *
 *   Pre-market · $514.10 · +0.35% · delayed 15 min (7:42 ET)
 *   Latest · $332.94 · delayed 15 min (10:12 ET)
 *   After hours · Today +$120 · delayed 16 min (5:05 ET)
 */
const SESSION_TEXT: Record<SessionLabel, string> = {
  'pre-market': 'Pre-market',
  regular: 'Latest',
  'after-hours': 'After hours',
  closed: 'Latest',
};

/** Above this delay the bar is from an earlier session: show its day instead. */
const FRESH_MINUTES = 60;

const nyTime = (iso: string, withDay: boolean): string => {
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = withDay
    ? { timeZone: 'America/New_York', weekday: 'short', hour: 'numeric', minute: '2-digit' }
    : { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit', hour12: true };
  const s = d.toLocaleString('en-US', opts);
  // "7:42 AM" -> "7:42"; with a day keep "am/pm": "Fri 7:59 pm"
  return withDay ? s.replace(/\s?([AP])M$/, (_m, p: string) => ` ${p.toLowerCase()}m`).replace(',', '') : s.replace(/\s?[AP]M$/, '');
};

export const latestWhen = (barTime: string | null, delayedMinutes: number | null): string | null => {
  if (!barTime) return null;
  if (delayedMinutes != null && delayedMinutes <= FRESH_MINUTES) {
    return `delayed ${delayedMinutes} min (${nyTime(barTime, false)} ET)`;
  }
  return `as of ${nyTime(barTime, true)} ET`;
};

const signedPct = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(2)}%`;
const signedUsd = (v: number) => {
  const r = Math.round(v);
  return `${r > 0 ? '+' : r < 0 ? '−' : ''}$${Math.abs(r).toLocaleString('en-US')}`;
};

interface Props {
  session: SessionLabel | null;
  barTime: string | null;
  delayedMinutes: number | null;
  /** Latest delayed price (price chip). */
  price?: number | null;
  /** Change vs the previous close, percent points (shown outside the regular session). */
  changePct?: number | null;
  /** Latest P&L instead of a price (positions). */
  pnl?: number | null;
  pnlLabel?: string;
  className?: string;
  /** Rows: only the New York time; the delay goes in the tooltip. */
  compact?: boolean;
}

const LatestPriceChip: React.FC<Props> = ({ session, barTime, delayedMinutes, price, changePct, pnl, pnlLabel, className, compact }) => {
  const isPnl = pnl !== undefined;
  if (isPnl ? pnl == null : price == null) return null;
  const label = SESSION_TEXT[session ?? 'regular'];
  const fullWhen = latestWhen(barTime, delayedMinutes);
  const fresh = delayedMinutes != null && delayedMinutes <= FRESH_MINUTES;
  const when = compact && barTime && fresh ? `delayed (${nyTime(barTime, false)} ET)` : fullWhen;
  const parts: React.ReactNode[] = [<span key="l" className="lp-chip-k">{label}</span>];
  if (isPnl) {
    parts.push(
      <b key="v" className={pnl! > 0 ? 'is-gain' : pnl! < 0 ? 'is-loss' : undefined}>
        {pnlLabel ? `${pnlLabel} ` : ''}{signedUsd(pnl!)}
      </b>
    );
  } else {
    parts.push(<b key="v">${price!.toFixed(2)}</b>);
    if (changePct != null && session !== 'regular') {
      parts.push(<span key="c" className={changePct > 0 ? 'is-gain' : changePct < 0 ? 'is-loss' : undefined}>{signedPct(changePct)}</span>);
    }
  }
  if (when) parts.push(<span key="w" className="lp-chip-w">{when}</span>);
  const title = isPnl
    ? 'Your P&L on the latest delayed stock price (15 minutes delayed; pre-market and after-hours included). The option leg uses its last mark. The main figure uses the 15-min Price.'
    : 'Latest price from the data provider, 15 minutes delayed, pre-market and after-hours included. For display only: scores, yields and P&L use the Price.';
  const tip = fullWhen ? `${title} (${fullWhen})` : title;
  return (
    <span className={`lp-chip ${className ?? ''}`} title={tip}>
      {parts.map((p, i) => (
        <React.Fragment key={i}>{i > 0 && <span className="lp-chip-sep" aria-hidden="true"> · </span>}{p}</React.Fragment>
      ))}
    </span>
  );
};

export default LatestPriceChip;
